import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Avatar,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Close,
  Restaurant,
  RoomService,
  LocalLaundryService,
  Spa,
  Person,
  Room,
  AccessTime,
  AttachMoney,
  CalendarToday,
  CheckCircle,
  Print,
  Email,
} from '@mui/icons-material';

export default function ViewServiceOrderDialog({ open, onClose, order }) {
  if (!order) return null;

  const getServiceIcon = (type) => {
    switch (type) {
      case 'Restaurant': return <Restaurant />;
      case 'Room Service': return <RoomService />;
      case 'Laundry': return <LocalLaundryService />;
      case 'Spa': return <Spa />;
      default: return <RoomService />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Confirmed': return 'info';
      case 'Pending': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar sx={{ bgcolor: '#1976d2', width: 48, height: 48 }}>
              {getServiceIcon(order.type)}
            </Avatar>
            <Box>
              <Typography variant="h6">Service Order Details</Typography>
              <Typography variant="caption" color="text.secondary">
                Order ID: {order.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Service Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                SERVICE INFORMATION
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Avatar sx={{ bgcolor: '#1976d2', width: 32, height: 32 }}>
                      {getServiceIcon(order.type)}
                    </Avatar>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Service Type
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.type}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CheckCircle color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Box>
                        <Chip
                          label={order.status}
                          size="small"
                          color={getStatusColor(order.status)}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Guest & Room Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                GUEST & ROOM DETAILS
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Person color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Guest Name
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.guest}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Room color="action" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Room Number
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.room}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Order Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                ORDER DETAILS
              </Typography>
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Items / Service Details
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {order.items}
                </Typography>
                {order.notes && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Additional Notes
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {order.notes}
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Date, Time & Amount */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, bgcolor: '#f5f7fa' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                SCHEDULING & PAYMENT
              </Typography>
              <Grid container spacing={2} mt={1}>
                <Grid item xs={12} sm={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {new Date(order.date).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AccessTime color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Time
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {order.time}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoney color="action" fontSize="small" />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Amount
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ${order.amount}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={<Print />}
                size="small"
                onClick={() => window.print()}
              >
                Print Order
              </Button>
              <Button
                variant="outlined"
                startIcon={<Email />}
                size="small"
              >
                Send to Guest
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
